import { IObservableArray, makeAutoObservable, observable } from 'mobx';
import { ApportionmentApiResponse } from '../../api/ApportionmentApi';
import { RuleApi, RuleApiResponse, RuleMatcher } from '../../api/RuleApi';
import { SourceType } from '../../api/SourceApi';
import translate, { Translations } from '../../language/translate';
import { Apportionment } from './apportionments/Apportionment';
import {
  BooleanCondition,
  CostHandler,
  FixedAmountApplicationType,
  MatchCondition,
  MatchType,
  RuleTransactionType,
  RuleType,
  RuleVisibility,
} from '../../config/Constants';
import { AssignmentSetting } from './apportionments/AssignmentSetting';
import { stringEmpty } from '../../utils/Strings';
import { AxiosError } from 'axios';
import { ApiError } from '../../api/Api';

export enum RuleValidationErrors {
  NON_UNIQUE_NAME = 'NON_UNIQUE_NAME',
}

export class TransactionMatcher {
  type: MatchType = MatchType.NOMINAL_CODE;
  condition: MatchCondition = MatchCondition.ONE_OF;
  value: string[] = [];
  error: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: RuleMatcher): TransactionMatcher {
    const matcher = new TransactionMatcher();
    matcher.type = apiData.matchFieldType as MatchType;
    matcher.condition = apiData.matchType as MatchCondition;
    matcher.value = apiData.values;
    return matcher;
  }

  toApi = (): RuleMatcher => ({
    matchFieldType: this.type,
    matchType: this.condition,
    values: this.value,
  });
}

export class Rule {
  id: number = 0;
  ruleApportionmentId: number = 0;
  groupId: number = 0;
  name: string = '';
  creatingEntity: number = 0;
  nominalCode: string = '';
  trackingCategories: Map<string, string> = new Map();
  type: RuleType = RuleType.COST;
  markup: number = 0;
  version: string = '0';
  rechargePercentage: number = 0;
  matchBooleanCondition: BooleanCondition = BooleanCondition.ALL;
  transactionMatchers: IObservableArray<TransactionMatcher> =
    [] as IObservableArray<TransactionMatcher>;
  apportionment: Apportionment = null;
  assignmentSettings: Map<number, AssignmentSetting> = new Map();
  isLatest: boolean = true;
  applyToUnmatched: boolean = false;
  costHandler: CostHandler = CostHandler.NET;
  transactionSource: SourceType = SourceType.xero;
  fixedAmount: number = 0;
  fixedAmountApplication: FixedAmountApplicationType =
    FixedAmountApplicationType.total;
  visibility: RuleVisibility = RuleVisibility.GROUP;

  errors: string[] = [];

  constructor() {
    makeAutoObservable(this);

    // Add an empty transaction matcher & apportionment
    this.transactionMatchers.push(new TransactionMatcher());
  }

  static fromApi(apiData: RuleApiResponse, groupId: number): Rule {
    const rule = new Rule();
    rule.groupId = groupId;
    rule.parseFromApi(apiData);
    return rule;
  }

  parseFromApi = (apiData: RuleApiResponse): void => {
    this.id = apiData.id;
    this.ruleApportionmentId = apiData.ruleApportionmentId;
    this.name = apiData.name;
    this.version = apiData.version;
    this.creatingEntity = apiData.organisationId;
    this.nominalCode = apiData.nominalCode;
    this.trackingCategories = apiData.trackingCategories
      ? new Map(apiData.trackingCategories.map(cat => [cat.split(':')[0], cat]))
      : new Map();
    this.type = apiData.type as RuleType;

    if (apiData.apportionment) {
      this.apportionment = Apportionment.fromApi(
        apiData.apportionment as ApportionmentApiResponse,
        this.groupId
      );
    }

    if (apiData.assignmentSettings) {
      this.assignmentSettings = new Map(
        apiData.assignmentSettings.map(setting => [
          setting.id,
          AssignmentSetting.fromApi(setting),
        ])
      );
    }

    this.rechargePercentage = apiData.percentage;
    this.matchBooleanCondition = apiData.applyAll
      ? BooleanCondition.ALL
      : BooleanCondition.ANY;
    this.applyToUnmatched = apiData.applyToUnmatched;
    this.costHandler = apiData.costHandler as CostHandler;
    this.transactionSource = apiData.transactionSource as SourceType;
    this.fixedAmount = apiData.fixedAmount;
    this.fixedAmountApplication =
      apiData.fixedAmountApplication as FixedAmountApplicationType;

    this.transactionMatchers = observable(
      apiData.matchers.map(matcher => TransactionMatcher.fromApi(matcher))
    );
    this.visibility = apiData.visibility;
  };

  toApi = (): RuleApiResponse => {
    // Filter the assignment settings down to just the valid ones
    const assignmentIds = this.apportionment?.assignmentIds();
    const assignments = [...this.assignmentSettings.values()]
      .filter(v => assignmentIds.includes(v.id) && !v.isEmpty())
      .map(v => v.toApi());

    return {
      id: this.id !== null && this.id !== 0 ? this.id : null,
      name: this.name,
      organisationId: this.creatingEntity,
      nominalCode: `${this.nominalCode}`,
      trackingCategories: [...this.trackingCategories.values()],
      type: this.type,
      percentage: this.rechargePercentage,
      // Apportionment needs to be saved separately, if it hasn't by here then
      // we must be doing a none-type with a null apportionment
      apportionment:
        this.apportionment?.id < 0 ? null : this.apportionment?.toApi(),
      assignmentSettings: assignments,
      matchers: this.transactionMatchers.map(matcher => matcher.toApi()),
      applyAll: this.matchBooleanCondition === BooleanCondition.ALL,
      applyToUnmatched: this.applyToUnmatched,
      costHandler: this.costHandler,
      transactionSource: this.transactionSource,
      fixedAmount: this.fixedAmount,
      fixedAmountApplication: this.fixedAmountApplication,
      visibility: this.visibility,
    };
  };

  prettyType = (): string => translate(Translations.Rules.Types[this.type]) as string;

  get rechargeEntities(): number[] {
    if (!this.apportionment) return [];
    return this.apportionment.entities(this.assignmentSettings);
  }

  get isCostBased(): boolean {
    return (
      this.type === RuleType.COST ||
      this.type === RuleType.COST_PLUS ||
      this.type === RuleType.NONE
    );
  }

  get isSalesBased(): boolean {
    return this.type === RuleType.SALES || this.type === RuleType.NONE_SALES;
  }

  get isBalanceSheetBased(): boolean {
    return this.type === RuleType.VALUE || this.type === RuleType.NONE_BALANCE;
  }

  get isMaydayRule(): boolean {
    return this.type === RuleType.FIXED;
  }

  get isNoneType(): boolean {
    return [RuleType.NONE, RuleType.NONE_BALANCE, RuleType.NONE_SALES].includes(
      this.type
    );
  }

  get transactionType(): RuleTransactionType {
    switch (this.type) {
      case RuleType.COST:
      case RuleType.COST_PLUS:
      case RuleType.NONE:
        return RuleTransactionType.expense;
      case RuleType.SALES:
      case RuleType.NONE_SALES:
        return RuleTransactionType.income;
      case RuleType.VALUE:
      case RuleType.NONE_BALANCE:
        return RuleTransactionType.balancesheet;
      case RuleType.FIXED:
        return RuleTransactionType.none;
    }
  }

  save = (): Promise<Rule> => {
    return RuleApi.updateRule(
      this.groupId,
      this.creatingEntity,
      this.id,
      this.toApi()
    )
      .then((data: RuleApiResponse) => {
        this.parseFromApi(data);
        return this;
      })
      .catch(this.checkValidationErrors);
  };

  create = (): Promise<Rule> => {
    return RuleApi.createRule(this.groupId, this.creatingEntity, this.toApi())
      .then((data: RuleApiResponse) => {
        this.parseFromApi(data);
        return this;
      })
      .catch(this.checkValidationErrors);
  };

  checkValidationErrors = (e: AxiosError<ApiError>) => {
    if (
      e.response?.data?.message?.includes(RuleValidationErrors.NON_UNIQUE_NAME)
    ) {
      this.errors.push('unique-name');
    }
    throw e;
  };

  assignmentSettingsFor(assignmentId: number): AssignmentSetting {
    if (!this.assignmentSettings.has(assignmentId)) {
      const setting = new AssignmentSetting();
      setting.id = assignmentId;
      this.assignmentSettings.set(assignmentId, setting);
    }
    return this.assignmentSettings.get(assignmentId);
  }

  removeAssignmentSettingsFor(assignmentId: number): AssignmentSetting {
    const assignmentSetting = this.assignmentSettings.get(assignmentId);
    this.assignmentSettings.delete(assignmentId);
    return assignmentSetting;
  }
}

export const validateRule = (rule: Rule, custom: boolean): string[] => {
  const errors = [];

  if (stringEmpty(rule.name)) {
    errors.push('name');
  }

  if (rule.name.length > 250) {
    errors.push('length-name');
  }

  if (
    rule.type === RuleType.COST_PLUS ||
    rule.type === RuleType.FIXED ||
    (rule.type === RuleType.SALES && rule.costHandler === CostHandler.CODE)
  ) {
    if (stringEmpty(rule.nominalCode)) {
      errors.push('nominalCode');
    }
  }

  if (
    rule.type !== RuleType.NONE &&
    rule.type !== RuleType.NONE_SALES &&
    rule.type !== RuleType.NONE_BALANCE
  ) {
    if (
      !rule.apportionment ||
      rule.apportionment.validate(
        rule.creatingEntity,
        rule.assignmentSettings,
        rule.type === RuleType.SALES
      )
    ) {
      errors.push('apportionments');
    }
  }

  if (rule.type !== RuleType.FIXED && !rule.applyToUnmatched && !custom) {
    rule.transactionMatchers.forEach(matcher => {
      if (matcher.value.length === 0) {
        matcher.error = true;
        errors.push('matchers');
      } else {
        matcher.error = false;
      }
    });
  }

  return errors;
};

export const cloneTrackingCategoryMap = (
  trackingCategories: Map<string, string>
) => new Map(trackingCategories.entries());
